import React, { useState, useMemo, useEffect } from "react";

// ═════════════════════════════════════════════════════════════════════════════
// MAPPING DASHBOARD - Professional Geolocation UI
// ═════════════════════════════════════════════════════════════════════════════

const MappingDashboard = ({ isDark = false }) => {
  const theme = isDark ? {
    bg: "#0f1419",
    surface: "#1a1f26",
    border: "#282e38",
    text: "#e4e6eb",
    textSecondary: "#8a8d91",
    primary: "#0a66c2",
    primaryLight: "#0a66c250",
    success: "#31a24c",
    danger: "#e85d75",
    warning: "#f5a623",
  } : {
    bg: "#ffffff",
    surface: "#f8f9fa",
    border: "#e5e7eb",
    text: "#1a1a1a",
    textSecondary: "#6b7280",
    primary: "#0a66c2",
    primaryLight: "#0a66c215",
    success: "#31a24c",
    danger: "#e85d75",
    warning: "#f5a623",
  };

  // Mock data
  const mockLocations = [
    { id: 1, type: "restaurant", name: "Gwalior Restaurant", lat: 26.2183, lng: 75.8267, address: "Gwalior, Madhya Pradesh" },
    { id: 2, type: "restaurant", name: "New Delhi Dhaba", lat: 28.7041, lng: 77.1025, address: "New Delhi" },
    { id: 3, type: "restaurant", name: "Lucknow Cuisine", lat: 26.8467, lng: 80.9462, address: "Lucknow, Uttar Pradesh" },
    { id: 4, type: "address", name: "Hazratganj, Connaught Place", lat: 28.6292, lng: 77.2197, address: "New Delhi, Delhi" },
    { id: 5, type: "pickup", name: "Order Pickup Point", lat: 26.2389, lng: 75.8659, address: "Gwalior Central" },
    { id: 6, type: "drop", name: "Order Drop Point", lat: 28.5921, lng: 77.2064, address: "Delhi South" },
    { id: 7, type: "restaurant", name: "Amritsar Corner", lat: 31.6340, lng: 74.8723, address: "Amritsar, Punjab" },
    { id: 8, type: "address", name: "CP Delhi", lat: 28.6305, lng: 77.2245, address: "Connaught Place, Delhi" },
    { id: 9, type: "restaurant", name: "Jaipur Royal", lat: 26.9124, lng: 75.7873, address: "Jaipur, Rajasthan" },
  ];

  const [locations, setLocations] = useState(mockLocations);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedState, setSelectedState] = useState("all");
  const [mapCenter, setMapCenter] = useState({ lat: 26.2183, lng: 75.8267 });
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Filter locations
  const filteredLocations = useMemo(() => {
    return locations.filter(loc => {
      const matchesSearch = 
        loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.address.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = selectedType === "all" || loc.type === selectedType;
      return matchesSearch && matchesType;
    });
  }, [locations, searchTerm, selectedType]);

  // Statistics
  const stats = useMemo(() => ({
    total: locations.length,
    restaurants: locations.filter(l => l.type === "restaurant").length,
    addresses: locations.filter(l => l.type === "address").length,
    pickups: locations.filter(l => l.type === "pickup").length,
    drops: locations.filter(l => l.type === "drop").length,
    indexed: 582,
    newThisWeek: 15,
  }), [locations]);

  const handleSelectLocation = (location) => {
    setSelectedLocation(location);
    setMapCenter({ lat: location.lat, lng: location.lng });
  };

  const handleDeleteLocation = (id) => {
    if (window.confirm("Delete this location?")) {
      setLocations(locations.filter(l => l.id !== id));
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case "restaurant": return "🍔";
      case "address": return "📍";
      case "pickup": return "📦";
      case "drop": return "📭";
      default: return "📌";
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case "restaurant": return "Restaurant";
      case "address": return "Address";
      case "pickup": return "Pickup Point";
      case "drop": return "Drop Point";
      default: return type;
    }
  };

  const ServiceCard = ({ name, service, status, message }) => (
    <div style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      padding: "14px",
      flex: 1,
      minWidth: "200px",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <div style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          background: status === "online" ? theme.success + "30" : theme.danger + "30",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "12px",
          color: status === "online" ? theme.success : theme.danger,
        }}>
          {status === "online" ? "✓" : "✕"}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: theme.text, marginBottom: "2px" }}>
            {name}
          </div>
          <div style={{ fontSize: "11px", color: theme.textSecondary }}>
            {service}
          </div>
          <div style={{ fontSize: "10px", color: theme.danger, marginTop: "4px" }}>
            {message}
          </div>
        </div>
      </div>
    </div>
  );

  const DataCategory = ({ icon, label, count, isActive, onClick }) => (
    <button
      onClick={onClick}
      style={{
        padding: "10px 16px",
        background: isActive ? theme.primary : theme.surface,
        color: isActive ? "white" : theme.text,
        border: `1px solid ${isActive ? theme.primary : theme.border}`,
        borderRadius: "8px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.target.style.background = theme.primaryLight;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.target.style.background = theme.surface;
        }
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
      <span style={{ opacity: 0.7 }}>({count})</span>
    </button>
  );

  const LocationCard = ({ location, isSelected }) => (
    <div
      onClick={() => handleSelectLocation(location)}
      style={{
        background: isSelected ? theme.primary + "15" : theme.surface,
        border: `1px solid ${isSelected ? theme.primary : theme.border}`,
        borderRadius: "10px",
        padding: "12px",
        marginBottom: "8px",
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = theme.primary;
        e.currentTarget.style.boxShadow = `0 2px 8px ${theme.primary}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isSelected ? theme.primary : theme.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "13px", fontWeight: 600, color: theme.text, marginBottom: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>{getIcon(location.type)}</span>
          {location.name}
        </div>
        <div style={{ fontSize: "11px", color: theme.textSecondary }}>
          {location.address}
        </div>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDeleteLocation(location.id);
        }}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "16px",
          opacity: 0.5,
          transition: "all 0.2s",
        }}
        onMouseEnter={(e) => {
          e.target.style.opacity = "1";
          e.target.style.color = theme.danger;
        }}
        onMouseLeave={(e) => {
          e.target.style.opacity = "0.5";
          e.target.style.color = "inherit";
        }}
      >
        🗑️
      </button>
    </div>
  );

  return (
    <div style={{
      minHeight: "100vh",
      background: theme.bg,
      padding: "24px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      color: theme.text,
    }}>
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        button { transition: all 0.2s ease; cursor: pointer; }
        input:focus { outline: none; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${theme.surface}; }
        ::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 3px; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            background: theme.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "24px",
          }}>
            📍
          </div>
          <div>
            <h1 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "2px" }}>Self-Hosted Maps</h1>
            <div style={{ fontSize: "12px", color: theme.textSecondary }}>OSRM + Nominatim</div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px", alignItems: "center" }}>
            <div style={{ fontSize: "12px", color: theme.success, fontWeight: 600 }}>🟢 1/3 Online</div>
            <div style={{ fontSize: "12px", color: theme.textSecondary }}>.215ms avg</div>
          </div>
        </div>

        {/* Service Status */}
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <ServiceCard
            name="OSRM"
            service="Distance & Routing"
            status="online"
            message="215ms - Route engine ready"
          />
          <ServiceCard
            name="Nominatim (Place Search)"
            service="offline"
            status="offline"
            message="offline - Unexpected token '<'"
          />
          <ServiceCard
            name="Nominatim (Reverse Geocoding)"
            service="offline"
            status="offline"
            message="offline - Unexpected token '<'"
          />
        </div>
      </div>

      {/* Data Section */}
      <div style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "24px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <h2 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
              📊 Our Map Data
              <span style={{ fontSize: "12px", color: theme.textSecondary, fontWeight: 500 }}>
                {stats.indexed} places indexed
              </span>
              <span style={{ fontSize: "12px", color: theme.success, fontWeight: 500 }}>
                +{stats.newThisWeek} this week
              </span>
              <span style={{ fontSize: "12px", color: theme.success, fontWeight: 600 }}>
                ✓ Privacy-safe
              </span>
            </h2>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <select style={{
              padding: "8px 12px",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: "6px",
              color: theme.text,
              fontSize: "12px",
              cursor: "pointer",
            }}>
              <option>All states</option>
              <option>Madhya Pradesh</option>
              <option>Delhi</option>
              <option>Uttar Pradesh</option>
            </select>
            <select style={{
              padding: "8px 12px",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: "6px",
              color: theme.text,
              fontSize: "12px",
              cursor: "pointer",
            }}>
              <option>All cities</option>
            </select>
            <button style={{
              padding: "8px 12px",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: "6px",
              color: theme.text,
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
            }}>
              🔄
            </button>
          </div>
        </div>

        {/* Data Categories */}
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <DataCategory
            icon="🍔"
            label="Restaurants"
            count={stats.restaurants}
            isActive={selectedType === "restaurant"}
            onClick={() => setSelectedType("restaurant")}
          />
          <DataCategory
            icon="📍"
            label="Customer Addresses"
            count={stats.addresses}
            isActive={selectedType === "address"}
            onClick={() => setSelectedType("address")}
          />
          <DataCategory
            icon="📦"
            label="Order Pickups"
            count={stats.pickups}
            isActive={selectedType === "pickup"}
            onClick={() => setSelectedType("pickup")}
          />
          <DataCategory
            icon="📭"
            label="Order Drops"
            count={stats.drops}
            isActive={selectedType === "drop"}
            onClick={() => setSelectedType("drop")}
          />
          <div style={{ marginLeft: "auto", fontSize: "12px", color: theme.textSecondary, display: "flex", alignItems: "center" }}>
            Showing {filteredLocations.length} in view
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ display: "flex", gap: "24px", minHeight: "600px" }}>
        
        {/* Left - Map */}
        <div style={{ flex: 1, minWidth: "400px" }}>
          <div style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: "12px",
            overflow: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}>
            {/* Map Area */}
            <div style={{
              flex: 1,
              background: "linear-gradient(135deg, #d4e4f7 0%, #e8f0f8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}>
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"100\" height=\"100\"><rect fill=\"%23e5f0ff\" width=\"100\" height=\"100\"/><circle cx=\"50\" cy=\"50\" r=\"30\" fill=\"%23d4e4f7\" opacity=\"0.1\"/></svg>')",
                opacity: 0.5,
              }} />
              
              <div style={{ textAlign: "center", zIndex: 2 }}>
                <div style={{ fontSize: "64px", marginBottom: "12px" }}>🗺️</div>
                <h3 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "4px" }}>Interactive Map View</h3>
                <p style={{ fontSize: "12px", color: theme.textSecondary }}>
                  Center: {mapCenter.lat.toFixed(4)}, {mapCenter.lng.toFixed(4)}
                </p>
                <p style={{ fontSize: "11px", color: theme.textSecondary, marginTop: "8px" }}>
                  {filteredLocations.length} locations on map
                </p>
              </div>

              {/* Map Controls */}
              <div style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                zIndex: 3,
              }}>
                <button style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "white",
                  border: `1px solid ${theme.border}`,
                  cursor: "pointer",
                  fontSize: "18px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}>
                  ➕
                </button>
                <button style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "white",
                  border: `1px solid ${theme.border}`,
                  cursor: "pointer",
                  fontSize: "18px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}>
                  ➖
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right - Search & List */}
        <div style={{ flex: 1, minWidth: "320px", maxHeight: "100vh", overflowY: "auto" }}>
          <div style={{
            background: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: "12px",
            padding: "16px",
          }}>
            {/* Search Tabs */}
            <div style={{
              display: "flex",
              gap: "12px",
              marginBottom: "16px",
              borderBottom: `1px solid ${theme.border}`,
              paddingBottom: "12px",
            }}>
              <button style={{
                padding: "8px 16px",
                background: "transparent",
                color: theme.primary,
                border: "none",
                borderBottom: `2px solid ${theme.primary}`,
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
              }}>
                🔍 Search
              </button>
              <button style={{
                padding: "8px 16px",
                background: "transparent",
                color: theme.textSecondary,
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
              }}>
                🛣️ Route
              </button>
              <button style={{
                padding: "8px 16px",
                background: "transparent",
                color: theme.textSecondary,
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 600,
              }}>
                🔄 Reverse
              </button>
            </div>

            {/* Search Input */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ position: "relative", marginBottom: "8px" }}>
                <input
                  type="text"
                  placeholder="Search any place in India — tap a result to fly the map there"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: theme.text,
                  }}
                  onFocus={(e) => e.target.style.borderColor = theme.primary}
                  onBlur={(e) => e.target.style.borderColor = theme.border}
                />
                <div style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", fontSize: "16px", opacity: 0.5 }}>
                  🔍
                </div>
              </div>
              <input
                type="text"
                placeholder="Hazratganj, Connaught Place, ..."
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: theme.textSecondary,
                }}
              />
            </div>

            {/* Search Message */}
            <div style={{
              padding: "12px",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: "8px",
              fontSize: "11px",
              color: theme.textSecondary,
              textAlign: "center",
              marginBottom: "16px",
            }}>
              Type at least 2 characters
            </div>

            {/* Locations List */}
            <div>
              <h3 style={{ fontSize: "12px", fontWeight: 700, color: theme.textSecondary, margin: "0 0 12px 0", textTransform: "uppercase" }}>
                Results ({filteredLocations.length})
              </h3>
              {filteredLocations.length === 0 ? (
                <div style={{ textAlign: "center", padding: "24px 12px", color: theme.textSecondary }}>
                  <p style={{ fontSize: "13px" }}>No locations found</p>
                </div>
              ) : (
                filteredLocations.map(location => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    isSelected={selectedLocation?.id === location.id}
                  />
                ))
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MappingDashboard;